import * as React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";

/* Anatomi brand SoalHub pada stock Button — unifikasi tombol project
 * (2026-09-25, PIVOT SOFT eks neo-brutalist): rounded-xl, font-bold, border
 * 1px edge halus + soft shadow tier token (--shadow-soft-*), feedback
 * hover-lift / active-dip (shadow mengecil). SATU komponen untuk seluruh app;
 * perbedaan antar tombol HANYA via variant (warna) + size. Aturan #10788:
 * nilai size stock (h-6/7/8/9) TIDAK diubah — komponen internal (Calendar,
 * Combobox, button-group) bergantung padanya; tombol app memakai size
 * tambahan xl/2xl. Eks BrandButton + overlay className. */
const buttonVariants = cva(
  "group group/button inline-flex shrink-0 items-center justify-center rounded-xl border border-transparent bg-clip-padding text-sm font-bold whitespace-nowrap transition-all duration-150 outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 hover:-translate-y-0.5 active:not-aria-[haspopup]:translate-y-px active:shadow-soft-xs disabled:pointer-events-none disabled:opacity-50 disabled:hover:translate-y-0 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border-primary-edge bg-primary text-primary-foreground shadow-soft [--soft-shadow-color:var(--color-primary-edge)] hover:bg-primary/90 hover:shadow-soft-lg",
        outline:
          "border-border bg-card shadow-soft-md [--soft-shadow-color:var(--border)] hover:bg-accent hover:text-accent-foreground hover:shadow-soft aria-expanded:bg-accent aria-expanded:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "border-secondary-edge bg-secondary text-secondary-foreground shadow-soft [--soft-shadow-color:var(--color-secondary-edge)] hover:bg-secondary/85 hover:shadow-soft-lg aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "border-destructive-edge bg-destructive text-destructive-foreground shadow-soft [--soft-shadow-color:var(--color-destructive-edge)] hover:bg-destructive/90 hover:shadow-soft-lg focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "text-primary underline-offset-4 hover:underline active:shadow-none",
        /* Signature app — eks "Coba Simulasi" (2026-09-25): glass untuk
         * surface berwarna/foto; shadow lembut sewarna, "cuma beda warna"
         * seperti semua variant — brand-800 agar terbaca di atas gradien. */
        glass:
          "border-primary-foreground/20 bg-primary-foreground/10 text-primary-foreground shadow-soft [--soft-shadow-color:var(--brand-800)] backdrop-blur-sm hover:bg-primary-foreground/15 hover:text-primary-foreground hover:shadow-soft-lg",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xl: "h-11 gap-2 px-5 text-sm",
        "2xl": "h-12 gap-2.5 px-7 text-base",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
        "icon-xl": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * `nativeButton` dihitung otomatis dari prop `render`: elemen non-<button>
 * (mis. <Link/>) → false, agar Base UI tidak menyalakan semantik tombol
 * native (space/enter, form submit) pada elemen yang bukan tombol — dan
 * berhenti me-log error dev "nativeButton prop is true". Elemen <button>
 * asli → true; caller bisa selalu menimpa secara eksplisit. (Satu-satunya
 * penyesuaian non-stock di file ini — sisanya murni registry base-nova.)
 */
function resolveNativeButton(
  nativeButton: boolean | undefined,
  render: ButtonPrimitive.Props["render"],
): boolean | undefined {
  if (nativeButton !== undefined) return nativeButton;
  if (React.isValidElement(render)) {
    return typeof render.type === "string" && render.type === "button";
  }
  return undefined;
}

function Button({
  className,
  variant = "default",
  size = "default",
  nativeButton,
  render,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      nativeButton={resolveNativeButton(nativeButton, render)}
      render={render}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
